import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';

/** Distance (px) or speed above which a release commits to the next/prev slide (Tinder-style). */
const SWIPE_COMMIT_DISTANCE_PX = 130;
/** use-gesture velocity is px/ms; ~0.45 ≈ 450 px/s flick. */
const SWIPE_COMMIT_VELOCITY_PX_MS = 0.42;

/** Past this horizontal movement, raise “prev” above “next” so the correct neighbour sits under the top card. */
const STACK_BIAS_PX = 10;

const Z_TOP = 100;
const Z_TAIL = 0;
/** Prev/next relative stacking when both exist; “winner” sits directly under the active card. */
const Z_NEIGHBOUR_LOW = 1;
const Z_NEIGHBOUR_HIGH = 2;

function offscreenDistancePx() {
  if (typeof window === 'undefined') return 520;
  return Math.min(640, Math.max(380, window.innerWidth * 0.92));
}

/**
 * Stable z-index: DOM order is always tail → prev → next → top (never reorder).
 * Only z-index changes with drag direction so React does not shuffle keyed layers (avoids wrong artefact / Blob glitches).
 */
function stackLayerZIndex(kind, stackBias, hasPrev, hasNext) {
  if (kind === 'top') return Z_TOP;
  if (kind === 'tail') return Z_TAIL;
  const both = hasPrev && hasNext;
  if (!both) {
    return Z_NEIGHBOUR_HIGH;
  }
  if (stackBias === 'backward') {
    return kind === 'prev' ? Z_NEIGHBOUR_HIGH : Z_NEIGHBOUR_LOW;
  }
  return kind === 'next' ? Z_NEIGHBOUR_HIGH : Z_NEIGHBOUR_LOW;
}

/**
 * Card stack: fixed slot order + dynamic z-index so prev vs next sits under the active card when swiping.
 */
export default function LandingCarouselSwipeCard({
  swipeKey,
  peekPrevName,
  peekNextName,
  peekNextNextName,
  canSwipeNext,
  canSwipePrev,
  onSwipeNext,
  onSwipePrev,
  renderStackCard,
  className = '',
}) {
  const [{ x }, api] = useSpring(() => ({ x: 0 }));

  const [stackBias, setStackBias] = useState('forward');

  const canNextRef = useRef(canSwipeNext);
  const canPrevRef = useRef(canSwipePrev);
  const onNextRef = useRef(onSwipeNext);
  const onPrevRef = useRef(onSwipePrev);

  canNextRef.current = canSwipeNext;
  canPrevRef.current = canSwipePrev;
  onNextRef.current = onSwipeNext;
  onPrevRef.current = onSwipePrev;

  const hasPrev = Boolean(peekPrevName);
  const hasNext = Boolean(peekNextName);

  const stackSlots = useMemo(() => {
    const slots = [];
    if (peekNextNextName) slots.push({ name: peekNextNextName, kind: 'tail' });
    if (peekPrevName) slots.push({ name: peekPrevName, kind: 'prev' });
    if (peekNextName) slots.push({ name: peekNextName, kind: 'next' });
    slots.push({ name: swipeKey, kind: 'top' });
    return slots;
  }, [peekNextNextName, peekPrevName, peekNextName, swipeKey]);

  useEffect(() => {
    api.start({ x: 0, immediate: true });
  }, [swipeKey, api]);

  useEffect(() => {
    setStackBias('forward');
  }, [swipeKey]);

  const bind = useDrag(
    ({ active, movement: [mx], velocity: [vx] }) => {
      if (active) {
        if (mx > STACK_BIAS_PX) setStackBias('backward');
        else if (mx < -STACK_BIAS_PX) setStackBias('forward');

        let clamped = mx;
        if (!canNextRef.current && mx < 0) clamped = mx * 0.2;
        if (!canPrevRef.current && mx > 0) clamped = mx * 0.2;
        api.start({ x: clamped, immediate: true });
        return;
      }

      const distOk = Math.abs(mx) > SWIPE_COMMIT_DISTANCE_PX;
      const velOk = Math.abs(vx) > SWIPE_COMMIT_VELOCITY_PX_MS;
      const shouldCommit = distOk || velOk;
      const exit = offscreenDistancePx();

      if (shouldCommit && mx < 0 && canNextRef.current) {
        api.start({
          x: -exit,
          config: { tension: 210, friction: 26 },
          onRest: () => {
            onNextRef.current();
            api.start({ x: 0, immediate: true });
          },
        });
        return;
      }
      if (shouldCommit && mx > 0 && canPrevRef.current) {
        api.start({
          x: exit,
          config: { tension: 210, friction: 26 },
          onRest: () => {
            onPrevRef.current();
            api.start({ x: 0, immediate: true });
          },
        });
        return;
      }

      api.start({
        x: 0,
        config: { tension: 320, friction: 34 },
        onRest: () => setStackBias('forward'),
      });
    },
    {
      axis: 'lock',
      filterTaps: true,
      threshold: 14,
      pointer: { touch: true },
    }
  );

  if (typeof renderStackCard !== 'function') {
    return null;
  }

  return (
    <div className={`overview-carousel-stack${className ? ` ${className}` : ''}`}>
      {stackSlots.map(({ name, kind }) => {
        const isTop = kind === 'top';
        const z = stackLayerZIndex(kind, stackBias, hasPrev, hasNext);
        return (
          <animated.div
            key={name}
            className={
              isTop
                ? 'overview-carousel-stack-slot overview-carousel-stack-slot--top overview-carousel-drag-shell'
                : 'overview-carousel-stack-slot'
            }
            style={{
              zIndex: z,
              transform: isTop
                ? x.to((v) => `translate3d(${v}px,0,0)`)
                : 'translate3d(0px,0,0)',
              touchAction: isTop ? 'pan-y' : undefined,
              willChange: isTop ? 'transform' : undefined,
            }}
            {...(isTop ? bind() : {})}
          >
            {renderStackCard(name, { isTop })}
          </animated.div>
        );
      })}
    </div>
  );
}
